import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Resets scroll position to the top whenever the route's path changes.
 *
 * Most pages scroll the window, but the `SectionLayout` canvas variant (the
 * dashboard editor) scrolls an internal `<main id="main-content">` instead — so both are reset. This is
 * intentionally a plain "jump to top on navigation" behaviour, not full
 * scroll-position restoration (no per-route memory of where the user was),
 * which the app does not need today.
 *
 * Ignored on same-path updates (query string / hash changes, e.g. switching
 * a tab via `?tab=`) so in-page navigation doesn't yank the scroll position.
 */
export function useScrollRestoration() {
  const { pathname } = useLocation();
  const previousPathname = useRef<string | null>(null);

  useEffect(() => {
    if (previousPathname.current === pathname) return;
    previousPathname.current = pathname;

    window.scrollTo(0, 0);
    document.getElementById("main-content")?.scrollTo(0, 0);
  }, [pathname]);
}

/** Render once near the root of the router tree; renders nothing. */
export function ScrollToTop() {
  useScrollRestoration();
  return null;
}
